import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  LogOut,
  FileText,
  Users,
  TrendingUp,
  Sparkles,
  Plus,
  Loader,
} from "lucide-react";
import { adminAPI, blogAPI, leadAPI } from "../services/api";

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [admin, setAdmin] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [recentBlogs, setRecentBlogs] = useState([]);
  const [recentLeads, setRecentLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    loadDashboard();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadDashboard = async () => {
    try {
      const [adminData, analyticsData, blogs, leads] = await Promise.all([
        adminAPI.getMe(),
        leadAPI.getDashboard(),
        blogAPI.getAll({ limit: 5 }),
        leadAPI.getAll({ limit: 5 }),
      ]);

      setAdmin(adminData);
      setAnalytics(analyticsData);
      setRecentBlogs(blogs);
      setRecentLeads(leads);
    } catch (error) {
      console.error("Dashboard load error:", error);
      if (error.response?.status === 401) {
        localStorage.removeItem("admin_token");
        navigate("/admin/login");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateBlog = async () => {
    if (
      !window.confirm(
        "Generate a new AI blog post? This will use your OpenAI API quota.",
      )
    ) {
      return;
    }

    setGenerating(true);
    try {
      await blogAPI.generateAI();
      alert("Blog post generated successfully!");
      loadDashboard();
    } catch (error) {
      alert(
        "Failed to generate blog post: " +
          (error.response?.data?.detail || error.message),
      );
    } finally {
      setGenerating(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("admin_token");
    navigate("/admin/login");
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--background)]">
        <Loader size={48} className="text-[var(--primary)] animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--background)]">
      {/* Header */}
      <header className="bg-[var(--surface-container)] border-b border-[var(--outline-variant)]/20">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-4 flex items-center justify-between">
          <div>
            <Link
              to="/"
              className="font-headline text-xl font-bold text-[var(--primary)] uppercase tracking-headline"
            >
              Kravion
            </Link>
            <p className="font-body text-sm text-[var(--on-surface-variant)] mt-1">
              Admin Dashboard
            </p>
          </div>
          <div className="flex items-center gap-4">
            <span className="font-body text-sm text-[var(--on-surface-variant)]">
              {admin?.full_name}
            </span>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 px-4 py-2 bg-[var(--surface-container-high)] hover:bg-[var(--error-container)] text-[var(--on-surface)] hover:text-[var(--error)] transition-colors font-headline text-sm uppercase tracking-code"
            >
              <LogOut size={16} />
              Logout
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 lg:px-8 py-12">
        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-12">
          <div className="bg-[var(--surface-container)] p-6 border-l-4 border-[var(--primary)]">
            <div className="flex items-center justify-between mb-2">
              <FileText size={24} className="text-[var(--primary)]" />
              <span className="font-headline text-3xl font-bold text-[var(--on-surface)]">
                {analytics?.total_blog_views || 0}
              </span>
            </div>
            <p className="font-body text-xs tracking-code uppercase text-[var(--on-surface-variant)]">
              Blog Views
            </p>
          </div>

          <div className="bg-[var(--surface-container)] p-6 border-l-4 border-[var(--tertiary)]">
            <div className="flex items-center justify-between mb-2">
              <Users size={24} className="text-[var(--tertiary)]" />
              <span className="font-headline text-3xl font-bold text-[var(--on-surface)]">
                {analytics?.total_leads || 0}
              </span>
            </div>
            <p className="font-body text-xs tracking-code uppercase text-[var(--on-surface-variant)]">
              Total Leads
            </p>
          </div>

          <div className="bg-[var(--surface-container)] p-6 border-l-4 border-[var(--secondary)]">
            <div className="flex items-center justify-between mb-2">
              <TrendingUp size={24} className="text-[var(--secondary)]" />
              <span className="font-headline text-3xl font-bold text-[var(--on-surface)]">
                {analytics?.high_quality_leads || 0}
              </span>
            </div>
            <p className="font-body text-xs tracking-code uppercase text-[var(--on-surface-variant)]">
              High Quality Leads
            </p>
          </div>

          <div className="bg-[var(--surface-container)] p-6 border-l-4 border-[var(--primary)]">
            <div className="flex items-center justify-between mb-2">
              <FileText size={24} className="text-[var(--primary)]" />
              <span className="font-headline text-3xl font-bold text-[var(--on-surface)]">
                {analytics?.total_page_views || 0}
              </span>
            </div>
            <p className="font-body text-xs tracking-code uppercase text-[var(--on-surface-variant)]">
              Page Views
            </p>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="mb-12">
          <h2 className="font-headline text-2xl font-bold uppercase mb-6">
            Quick Actions
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <button
              onClick={handleGenerateBlog}
              disabled={generating}
              className="bg-gradient-to-br from-[var(--primary)] to-[var(--primary-container)] text-[var(--on-primary-container)] p-6 hover:brightness-110 transition-all disabled:opacity-50 flex items-center gap-4"
            >
              {generating ? (
                <Loader size={24} className="animate-spin" />
              ) : (
                <Sparkles size={24} />
              )}
              <div className="text-left">
                <p className="font-headline text-lg font-bold uppercase">
                  Generate AI Blog
                </p>
                <p className="font-body text-sm opacity-80">
                  {generating ? "Generating..." : "Create blog post with AI"}
                </p>
              </div>
            </button>

            <Link
              to="/admin/blogs/new"
              className="bg-[var(--surface-container)] hover:bg-[var(--surface-container-high)] text-[var(--on-surface)] p-6 transition-all flex items-center gap-4"
            >
              <Plus size={24} className="text-[var(--primary)]" />
              <div className="text-left">
                <p className="font-headline text-lg font-bold uppercase">
                  New Blog Post
                </p>
                <p className="font-body text-sm text-[var(--on-surface-variant)]">
                  Write manually
                </p>
              </div>
            </Link>

            <Link
              to="/admin/leads"
              className="bg-[var(--surface-container)] hover:bg-[var(--surface-container-high)] text-[var(--on-surface)] p-6 transition-all flex items-center gap-4"
            >
              <Users size={24} className="text-[var(--tertiary)]" />
              <div className="text-left">
                <p className="font-headline text-lg font-bold uppercase">
                  View Leads
                </p>
                <p className="font-body text-sm text-[var(--on-surface-variant)]">
                  Manage contacts
                </p>
              </div>
            </Link>
          </div>
        </div>

        {/* Recent Content */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Recent Blogs */}
          <div>
            <h3 className="font-headline text-xl font-bold uppercase mb-6">
              Recent Blog Posts
            </h3>
            <div className="space-y-4">
              {recentBlogs.length > 0 ? (
                recentBlogs.map((blog) => (
                  <div
                    key={blog.id}
                    className="bg-[var(--surface-container)] p-4 hover:bg-[var(--surface-container-high)] transition-all"
                  >
                    <div className="flex items-start justify-between mb-2">
                      <h4 className="font-headline font-bold text-sm line-clamp-1">
                        {blog.title}
                      </h4>
                      {blog.is_ai_generated && (
                        <span className="px-2 py-1 bg-[var(--primary)]/20 text-[var(--primary)] text-xs font-bold tracking-code uppercase rounded-sm flex-shrink-0 ml-2">
                          AI
                        </span>
                      )}
                    </div>
                    <p className="font-body text-xs text-[var(--on-surface-variant)] mb-2 line-clamp-2">
                      {blog.excerpt}
                    </p>
                    <div className="flex items-center gap-4 text-xs text-[var(--on-surface-variant)]">
                      <span>{blog.category}</span>
                      <span>•</span>
                      <span>{blog.views} views</span>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-[var(--on-surface-variant)] font-body">
                  No blog posts yet
                </p>
              )}
            </div>
          </div>

          {/* Recent Leads */}
          <div>
            <h3 className="font-headline text-xl font-bold uppercase mb-6">
              Recent Leads
            </h3>
            <div className="space-y-4">
              {recentLeads.length > 0 ? (
                recentLeads.map((lead) => (
                  <div
                    key={lead.id}
                    className="bg-[var(--surface-container)] p-4 hover:bg-[var(--surface-container-high)] transition-all"
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <h4 className="font-headline font-bold text-sm">
                          {lead.full_name}
                        </h4>
                        <p className="font-body text-xs text-[var(--on-surface-variant)]">
                          {lead.email}
                        </p>
                      </div>
                      <div className="text-right">
                        <div className="px-2 py-1 bg-[var(--tertiary)]/20 text-[var(--tertiary)] text-xs font-bold tracking-code uppercase rounded-sm">
                          Score: {lead.score}
                        </div>
                      </div>
                    </div>
                    <p className="font-body text-xs text-[var(--on-surface-variant)] line-clamp-2 mb-2">
                      {lead.query}
                    </p>
                    <div className="flex items-center gap-2">
                      {lead.company && (
                        <span className="text-xs text-[var(--on-surface-variant)]">
                          {lead.company}
                        </span>
                      )}
                      {lead.service_interest && (
                        <>
                          <span>•</span>
                          <span className="text-xs text-[var(--primary)]">
                            {lead.service_interest}
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-[var(--on-surface-variant)] font-body">
                  No leads yet
                </p>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AdminDashboard;
